/**
 * Dialog Component
 * Based on Radix UI Dialog primitive
 */

import { cn } from '../lib/utils';
import { useToneScopeClass } from '../lib/tone-scope';
import {
  DIALOG_BAR,
  DIALOG_MEDIA,
  DIALOG_SIZE,
  type DialogMediaIntent,
  type DialogSize,
} from '../lib/dialog-anatomy';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import * as React from 'react';

// Context to track composition state across dialog children
const DialogCompositionContext = React.createContext<{
  isComposing: () => boolean;
  setComposing: (composing: boolean) => void;
  justEndedComposing: () => boolean;
  markCompositionEnd: () => void;
}>({
  isComposing: () => false,
  setComposing: (_composing: boolean) => {
    /* noop default */
  },
  justEndedComposing: () => false,
  markCompositionEnd: () => {
    /* noop default */
  },
});

export const useDialogComposition = () => React.useContext(DialogCompositionContext);

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const composingRef = React.useRef(false);
  const justEndedRef = React.useRef(false);
  const endTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const contextValue = React.useMemo(
    () => ({
      isComposing: () => composingRef.current,
      setComposing: (composing: boolean) => {
        composingRef.current = composing;
      },
      justEndedComposing: () => justEndedRef.current,
      markCompositionEnd: () => {
        justEndedRef.current = true;
        if (endTimerRef.current) {
          clearTimeout(endTimerRef.current);
        }
        endTimerRef.current = setTimeout(() => {
          justEndedRef.current = false;
        }, 150);
      },
    }),
    []
  );

  return (
    <DialogCompositionContext.Provider value={contextValue}>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </DialogCompositionContext.Provider>
  );
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * The close (X) button is an icon, so its accessible name is `closeLabel` —
 * REQUIRED whenever the button is shown. It used to be optional, and most
 * dialogs shipped a nameless button (axe `button-name`, critical, WCAG 4.1.2).
 * A dialog without the X says so with `showCloseButton={false}`.
 */
export type DialogCloseButtonProps =
  | {
      showCloseButton?: true;
      /** Screen reader label for the close button. Pass a translated string. */
      closeLabel: string;
      /**
       * `onBrand` when the X sits on a brand-coloured header: white, with no
       * accent square behind it (the default drew a grey square with a faint
       * X on the purple — JW-34).
       */
      closeTone?: 'default' | 'onBrand';
    }
  | { showCloseButton: false; closeLabel?: never; closeTone?: never };

/**
 * `layout="scroll"`: a dialog taller than the screen keeps its header and its
 * footer (the approval, the action) in view while its `DialogBody` scrolls —
 * the one layout for long dialogs, instead of each hand-rolling
 * `max-h-[90vh] overflow-y-auto` and a sticky footer.
 */
export type DialogContentProps = React.ComponentProps<typeof DialogPrimitive.Content> &
  DialogCloseButtonProps & {
    layout?: 'default' | 'scroll';
    /** sm: a confirmation · md: a short form · lg: a form, a reading · xl: a wizard. */
    size?: DialogSize;
  };

function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeLabel,
  closeTone = 'default',
  layout = 'default',
  size,
  onEscapeKeyDown,
  ...props
}: DialogContentProps) {
  const scope = useToneScopeClass();
  const { isComposing } = useDialogComposition();

  const handleEscapeKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      const isCurrentlyComposing =
        (e as unknown as { isComposing: boolean }).isComposing || isComposing();

      if (isCurrentlyComposing) {
        e.preventDefault();
        return;
      }

      // An expanded combobox in the dialog (a city search, say) owns Escape:
      // the key closes ITS list. Radix hears Escape first — on the document, in
      // the capture phase — so without this the key a keyboard user presses to
      // dismiss the suggestions closed the whole dialog, answers and all.
      const target = e.target instanceof Element ? e.target : null;
      if (
        target?.getAttribute('role') === 'combobox' &&
        target.getAttribute('aria-expanded') === 'true'
      ) {
        e.preventDefault();
        return;
      }

      onEscapeKeyDown?.(e);
    },
    [isComposing, onEscapeKeyDown]
  );

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-layout={layout}
        className={cn(
          scope,
          'group/dialog bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
          layout === 'scroll' && 'flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0',
          size && DIALOG_SIZE[size],
          className
        )}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            data-tone={closeTone}
            className={cn(
              'ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-xs transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4',
              closeTone === 'onBrand'
                ? 'text-white/90 hover:text-white focus:ring-white'
                : 'data-[state=open]:bg-accent text-muted-foreground hover:text-foreground'
            )}
          >
            <XIcon aria-hidden="true" />
            <span className="sr-only">{closeLabel}</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/**
 * `variant="bar"`: the platform's header (DIALOG_BAR) — `media` (a
 * `DialogMedia` tile, or a partner's mark) beside the title and description,
 * a line under them. Used in a `layout="scroll"` dialog.
 */
function DialogHeader({
  className,
  variant = 'default',
  media,
  children,
  ...props
}: React.ComponentProps<'div'> & { variant?: 'default' | 'bar'; media?: React.ReactNode }) {
  if (variant === 'bar') {
    return (
      <div
        data-slot="dialog-header"
        data-variant="bar"
        className={cn(DIALOG_BAR.header, className)}
        {...props}
      >
        {media}
        <div className="min-w-0 flex-1 space-y-1">{children}</div>
      </div>
    );
  }
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'flex flex-col gap-2 text-center sm:text-left',
        'group-data-[layout=scroll]/dialog:shrink-0 group-data-[layout=scroll]/dialog:px-6 group-data-[layout=scroll]/dialog:pt-6 group-data-[layout=scroll]/dialog:pr-12 group-data-[layout=scroll]/dialog:pb-4',
        className
      )}
      {...props}
    >
      {media}
      {children}
    </div>
  );
}

/** The header's icon tile: the scope's tone, or caution / danger / success. */
function DialogMedia({
  icon: Icon,
  intent = 'tone',
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  intent?: DialogMediaIntent;
  className?: string;
}) {
  return (
    <span
      data-slot="dialog-media"
      data-intent={intent}
      aria-hidden="true"
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg',
        DIALOG_MEDIA[intent],
        className
      )}
    >
      <Icon className="size-5" />
    </span>
  );
}

/** A wizard's step strip, between the header and the body. */
function DialogSteps({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-steps" className={cn(DIALOG_BAR.steps, className)} {...props} />;
}

/**
 * `variant="bar"`: the platform's footer (DIALOG_BAR) — `start` on the left
 * (the way out: Cancel, Back, Close), the children on the right (one main
 * action; a second at most).
 */
function DialogFooter({
  className,
  variant = 'default',
  start,
  children,
  ...props
}: React.ComponentProps<'div'> & { variant?: 'default' | 'bar'; start?: React.ReactNode }) {
  if (variant === 'bar') {
    return (
      <div
        data-slot="dialog-footer"
        data-variant="bar"
        className={cn(DIALOG_BAR.footer, className)}
        {...props}
      >
        <div className="flex min-w-0 items-center gap-3">{start}</div>
        <div className="flex min-w-0 items-center justify-end gap-3">{children}</div>
      </div>
    );
  }
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        'group-data-[layout=scroll]/dialog:shrink-0 group-data-[layout=scroll]/dialog:border-t group-data-[layout=scroll]/dialog:border-border group-data-[layout=scroll]/dialog:bg-background group-data-[layout=scroll]/dialog:px-6 group-data-[layout=scroll]/dialog:py-4',
        className
      )}
      {...props}
    >
      {start}
      {children}
    </div>
  );
}

/**
 * The part of a `layout="scroll"` dialog that scrolls, between its header and
 * its footer. `variant="bar"`: under a bar header — 24px all round.
 */
function DialogBody({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & { variant?: 'default' | 'bar' }) {
  return (
    <div
      data-slot="dialog-body"
      data-variant={variant}
      className={cn(
        variant === 'bar' ? DIALOG_BAR.body : 'min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4',
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMedia,
  DialogOverlay,
  DialogPortal,
  DialogSteps,
  DialogTitle,
  DialogTrigger,
};
